import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import {
  Customer,
  CustomerGuestPassUsage,
  CustomerProfileDetails,
  CustomerSharedPass,
  getGuestPassEligibilityDisplay,
  getGuestPassSourceText,
  getGuestPassStatusDisplay,
  GuestPass,
  GuestPassFilters,
  GuestPassStatus
} from '../../../../../../../models/customer';
import {
  CustomerMembership,
  CustomerMembershipCustomer,
  CustomerMembershipUnlinkedCustomer,
  getCustomerMembershipStatusDisplay,
  getMembershipDurationDisplay,
  getMembershipStatusColorClass,
  getMembershipIntervalDisplay,
  getMembershipPurchaseTypeDisplay
} from '../../../../../../../models/membership';
import {
  CallbackResult,
  dateStringFromISO8601,
  objHasNonEmptyValues,
  propExists,
  propExistsAndIsNonEmpty
} from '../../../../../../../utils';
import {
  EditRecurringCustomerMembershipModalComponent
} from './modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-modal.component';
import {
  MEMBERSHIP_DETAILS_QUERY_PARAM,
  MembershipDetailsModalComponent
} from './modals/membership-details-modal/membership-details-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  EditNonRecurringCustomerMembershipModalComponent
} from './modals/edit-non-recurring-customer-membership-modal/edit-non-recurring-customer-membership-modal.component';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { EditCustomerEntryPassModalComponent } from './modals/edit-customer-entry-pass-modal/edit-customer-entry-pass-modal.component';
import {
  CreateCustomerEntryPassModalComponent
} from './modals/create-customer-entry-pass-modal/create-customer-entry-pass-modal.component';
import { CustomerEntryPass, CustomerEntryPassUnlinkedCustomer } from '../../../../../../../models/entry-pass';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { LinkCustomerEntryPassModalComponent } from './modals/link-customer-entry-pass-modal/link-customer-entry-pass-modal.component';
import {
  PurchaseCustomerEntryPassModalComponent
} from './modals/purchase-customer-entry-pass-modal/purchase-customer-entry-pass-modal.component';
import {
  PurchaseCustomerMembershipModalComponent
} from './modals/purchase-customer-membership-modal/purchase-customer-membership-modal.component';
import { InboxService } from '../../../../../../../services/inbox/inbox.service';
import {
  ReviewUnlinkedEntryPassCustomerModalComponent
} from '../../../../inbox/inbox-memberships-and-passes/inbox-unlinked-entry-passes/modals/review-unlinked-entry-pass-customer-modal/review-unlinked-entry-pass-customer-modal.component';
import {
  ReviewUnlinkedMembershipCustomerModalComponent
} from '../../../../inbox/inbox-memberships-and-passes/inbox-unlinked-memberships/modals/review-unlinked-membership-customer-modal/review-unlinked-membership-customer-modal.component';
import {
  ReviewMembershipPendingStaffApprovalModalComponent
} from '../../../../inbox/inbox-memberships-and-passes/inbox-memberships-pending-staff-approval/modals/review-unlinked-membership-customer-modal/review-membership-pending-staff-approval-modal.component';
import { ownerIsOnlyMember } from '../../../../../../../utils-models';
import {
  TransferCustomerEntryPassModalComponent
} from './modals/transfer-customer-entry-pass-modal/transfer-customer-entry-pass-modal.component';
import { CustomerEntryPassStatus } from '../../../../../../../models/entry-pass-enums';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { LocalStorage } from 'angular-web-storage';
import { ListResponse } from '../../../../../../../models/list-response';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CreateGuestPassModalComponent } from './modals/create-guest-pass-modal/create-guest-pass-modal.component';
import { ManageGuestPassModalComponent } from './modals/manage-guest-pass-modal/manage-guest-pass-modal.component';
import { CustomerMembershipStatus, MembershipDiscountType, MembershipPurchaseType } from '../../../../../../../models/membership-enums';

@Component({
  selector: 'app-memberships-and-passes',
  templateUrl: './memberships-and-passes-sub-list.component.html',
  styleUrls: ['./memberships-and-passes-sub-list.component.css']
})
export class MembershipsAndPassesSubListComponent implements OnInit, OnDestroy {

  public membershipsPageNum = 1;
  public entryPassesPageNum = 1;
  public ownedGuestPassesPageNum = 1;
  public guestPassUsagesPageNum = 1;
  public sharedPassesPageNum = 1;
  // Giving these project-unique names ensures they are not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeMemberships: number;
  @LocalStorage() public pageSizeEntryPasses: number;
  @LocalStorage() public pageSizeOwnedGuestPasses: number;
  @LocalStorage() public pageSizeGuestPassUsages: number;
  @LocalStorage() public pageSizeSharedPasses: number;
  public ownedGuestPassesStatus: GuestPassStatus | null = GuestPassStatus.available;

  public reloadMembershipsSubject = new Subject<void>();
  public reloadEntryPassesSubject = new Subject<void>();
  public reloadOwnedGuestPassesSubject = new Subject<void>();
  public reloadGuestPassUsagesSubject = new Subject<void>();
  public reloadSharedPassesSubject = new Subject<void>();

  public customerId: number;

  public customer: Customer;
  public customerProfileDetails: CustomerProfileDetails;
  public membershipsList: ListResponse<CustomerMembership>;
  public entryPassesList: ListResponse<CustomerEntryPass>;

  // Membership details modal (PRO-1245) deep-link state. The customer
  // membership ID of the open modal is mirrored in the URL so it can be shared.
  private membershipDetailsModalRef: NgbModalRef = null;
  private currentlyOpenMembershipDetailsId: number = null;
  private desiredMembershipDetailsId: number = null;
  private membershipDetailsQueryParamSub: Subscription = null;
  public ownedGuestPassesList: ListResponse<GuestPass>;
  public guestPassUsagesList: ListResponse<CustomerGuestPassUsage>;
  public sharedPassesList: ListResponse<CustomerSharedPass>;
  public loadingCustomer = true;
  public loadingCustomerProfileDetails = true;
  public loadingMemberships = true;
  public loadingEntryPasses = true;
  public loadingOwnedGuestPasses = true;
  public loadingGuestPassUsages = true;
  public loadingSharedPasses = true;
  public loadingUnlinkedMembershipCustomer = {};
  public loadingUnlinkedEntryPassCustomer = {};

  public customerNotFound = false;
  public serverError: number = null;

  public loadingMembershipChange = false;

  public successMessage = '';

  // Make relevant enums and library functions available to the template.
  public readonly CustomerMembershipStatus = CustomerMembershipStatus;

  /**
   * The recurring statuses whose row label is written out in the template;
   * any other falls back to the shared status label rather than rendering
   * nothing (PRO-1460). Add a status here when giving it bespoke wording.
   */
  public readonly recurringStatusesWithOwnLabel: CustomerMembershipStatus[] = [
    CustomerMembershipStatus.active,
    CustomerMembershipStatus.frozen,
    CustomerMembershipStatus.initialPaymentProcessing,
    CustomerMembershipStatus.awaitingPaymentMethod,
    CustomerMembershipStatus.renewalPaymentPending,
    CustomerMembershipStatus.renewalPaymentProcessing,
    CustomerMembershipStatus.renewalPaymentFailed,
    CustomerMembershipStatus.freezePaymentPending,
    CustomerMembershipStatus.freezePaymentProcessing,
    CustomerMembershipStatus.freezePaymentFailed,
    CustomerMembershipStatus.ended,
    CustomerMembershipStatus.deniedStaffApproval,
  ];

  public readonly getMembershipStatusColorClass =
    getMembershipStatusColorClass;
  public readonly CustomerEntryPassStatus = CustomerEntryPassStatus;
  public readonly MembershipPurchaseType = MembershipPurchaseType;
  public readonly MembershipDiscountType = MembershipDiscountType;
  public readonly GuestPassStatus = GuestPassStatus;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;
  public readonly objHasNonEmptyValues = objHasNonEmptyValues;
  public readonly getMembershipPurchaseTypeDisplay = getMembershipPurchaseTypeDisplay;
  public readonly getMembershipIntervalDisplay = getMembershipIntervalDisplay;
  public readonly getMembershipDurationDisplay = getMembershipDurationDisplay;
  public readonly ownerIsOnlyMember = ownerIsOnlyMember;
  public readonly getGuestPassStatusDisplay = getGuestPassStatusDisplay;
  public readonly getGuestPassSourceText = getGuestPassSourceText;
  public readonly getGuestPassEligibilityDisplay = getGuestPassEligibilityDisplay;
  public readonly getCustomerMembershipStatusDisplay = getCustomerMembershipStatusDisplay;
  public readonly Object = Object;
  public readonly parseFloat = parseFloat;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    public inboxService: InboxService,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private membershipService: MembershipService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    const routeParams = this.route.parent.snapshot.params;
    this.customerId = parseInt(routeParams.id, 10);
    if (isNaN(this.customerId)) {
      this.customerNotFound = true;
      return;
    }

    if (!this.pageSizeMemberships) {
      this.pageSizeMemberships = 10;
    }
    if (!this.pageSizeEntryPasses) {
      this.pageSizeEntryPasses = 10;
    }
    if (!this.pageSizeOwnedGuestPasses) {
      this.pageSizeOwnedGuestPasses = 10;
    }
    if (!this.pageSizeGuestPassUsages) {
      this.pageSizeGuestPassUsages = 10;
    }
    if (!this.pageSizeSharedPasses) {
      this.pageSizeSharedPasses = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeMemberships = this.pageSizeMemberships;
    // noinspection SillyAssignmentJS
    this.pageSizeEntryPasses = this.pageSizeEntryPasses;
    // noinspection SillyAssignmentJS
    this.pageSizeOwnedGuestPasses = this.pageSizeOwnedGuestPasses;
    // noinspection SillyAssignmentJS
    this.pageSizeGuestPassUsages = this.pageSizeGuestPassUsages;
    // noinspection SillyAssignmentJS
    this.pageSizeSharedPasses = this.pageSizeSharedPasses;

    this.reloadMembershipsSubject.subscribe(() => {
      this.loadingMemberships = true;
    });
    this.reloadMembershipsSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadMemberships(this.customerId);
    });

    // Open/close the membership details modal in response to the URL query
    // parameter, so the modal can be deep-linked and shared.
    this.membershipDetailsQueryParamSub =
      this.route.queryParamMap.subscribe(params => {
        this.readMembershipDetailsQueryParam(params);
        this.syncMembershipDetailsModal();
      });

    this.reloadEntryPassesSubject.subscribe(() => {
      this.loadingEntryPasses = true;
    });
    this.reloadEntryPassesSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadEntryPasses(this.customerId);
    });

    this.reloadOwnedGuestPassesSubject.subscribe(() => {
      this.loadingOwnedGuestPasses = true;
    });
    this.reloadOwnedGuestPassesSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadOwnedGuestPasses(this.customerId);
    });

    this.reloadGuestPassUsagesSubject.subscribe(() => {
      this.loadingGuestPassUsages = true;
    });
    this.reloadGuestPassUsagesSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadGuestPassUsages(this.customerId);
    });

    this.reloadSharedPassesSubject.subscribe(() => {
      this.loadingSharedPasses = true;
    });
    this.reloadSharedPassesSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadSharedPasses(this.customerId);
    });

    this.loadCustomer(this.customerId);
    this.loadCustomerProfileDetails(this.customerId);
    this.loadMemberships(this.customerId);
    this.loadEntryPasses(this.customerId);
    this.loadOwnedGuestPasses(this.customerId);
    this.loadGuestPassUsages(this.customerId);
    this.loadSharedPasses(this.customerId);
  }

  public editMembership(membership: CustomerMembership) {
    this.successMessage = '';

    let editModalRef: NgbModalRef;
    if (membership.purchase_type === MembershipPurchaseType.recurring) {
      editModalRef = this.modalService.open(
        EditRecurringCustomerMembershipModalComponent
      );
    } else {
      editModalRef = this.modalService.open(
        EditNonRecurringCustomerMembershipModalComponent
      );
    }
    editModalRef.componentInstance.membership = membership;
    editModalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.reloadMembershipsSubject.next();
    };
  }

  public openDetailsModal(membership: CustomerMembership) {
    this.openMembershipDetailsModal(membership, true);
  }

  private openMembershipDetailsModal(membership: CustomerMembership,
                                     writeUrl: boolean) {
    // Avoid opening a duplicate modal for the same membership.
    if (this.currentlyOpenMembershipDetailsId === membership.id) {
      return;
    }
    // Close any modal already open (e.g. when switching directly between
    // memberships via the URL).
    if (this.membershipDetailsModalRef) {
      this.membershipDetailsModalRef.dismiss();
    }

    this.currentlyOpenMembershipDetailsId = membership.id;
    if (writeUrl) {
      this.setMembershipDetailsQueryParam(membership.id);
    }

    const modalRef = this.modalService.open(
      MembershipDetailsModalComponent,
      { size: 'lg', scrollable: true }
    );
    this.membershipDetailsModalRef = modalRef;
    modalRef.componentInstance.membership = membership;
    modalRef.componentInstance.manageCallback = () => this.editMembership(membership);

    // When the modal closes (dismissal or close), clear the open-tracking state
    // and remove the URL query parameter. The guard ignores a stale close after
    // a different modal has already been opened.
    const handleClosed = () => {
      if (this.membershipDetailsModalRef === modalRef) {
        this.membershipDetailsModalRef = null;
        this.currentlyOpenMembershipDetailsId = null;
        this.clearMembershipDetailsQueryParam();
      }
    };
    modalRef.result.then(handleClosed, handleClosed);
  }

  private readMembershipDetailsQueryParam(params: ParamMap) {
    const rawValue = params.get(MEMBERSHIP_DETAILS_QUERY_PARAM);
    const parsed = rawValue ? parseInt(rawValue, 10) : NaN;
    this.desiredMembershipDetailsId = isNaN(parsed) ? null : parsed;
  }

  // Open or close the details modal so it matches the membership ID in the URL.
  private syncMembershipDetailsModal() {
    const desiredId = this.desiredMembershipDetailsId;

    if (desiredId === this.currentlyOpenMembershipDetailsId) {
      return;
    }

    if (desiredId === null) {
      if (this.membershipDetailsModalRef) {
        this.membershipDetailsModalRef.dismiss();
      }
      return;
    }

    // Use the membership from the loaded list when available, otherwise fetch
    // it by ID (it may be on another page, or the list may not have loaded yet).
    const loaded = this.membershipsList && this.membershipsList.results
      ? this.membershipsList.results.find(m => m.id === desiredId)
      : null;
    if (loaded) {
      this.openMembershipDetailsModal(loaded, false);
      return;
    }

    this.membershipService.getCustomerMembership(desiredId)
      .then(membership => {
        // Ignore if the URL changed while the fetch was in flight.
        if (this.desiredMembershipDetailsId === desiredId) {
          this.openMembershipDetailsModal(membership, false);
        }
      })
      .catch(() => {
        // The ID is invalid or inaccessible: leave the modal closed.
      });
  }

  private setMembershipDetailsQueryParam(membershipId: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [MEMBERSHIP_DETAILS_QUERY_PARAM]: membershipId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private clearMembershipDetailsQueryParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [MEMBERSHIP_DETAILS_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy() {
    if (this.membershipDetailsQueryParamSub) {
      this.membershipDetailsQueryParamSub.unsubscribe();
    }
  }

  public editEntryPass(entryPass: CustomerEntryPass) {
    this.successMessage = '';

    const modalRef = this.modalService.open(
      EditCustomerEntryPassModalComponent
    );
    modalRef.componentInstance.entryPass = entryPass;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.successMessage = result.text;
      this.reloadEntryPassesSubject.next();
    };
  }

  public linkEntryPass(entryPass: CustomerEntryPass) {
    this.successMessage = '';

    const editModalRef = this.modalService.open(
      LinkCustomerEntryPassModalComponent
    );
    editModalRef.componentInstance.entryPass = entryPass;
    editModalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.reloadEntryPassesSubject.next();
    };
  }

  public transferEntryPass(entryPass: CustomerEntryPass) {
    this.successMessage = '';

    const editModalRef = this.modalService.open(
      TransferCustomerEntryPassModalComponent
    );
    editModalRef.componentInstance.entryPass = entryPass;
    editModalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.reloadEntryPassesSubject.next();
    };
  }

  public openPurchaseMembershipModal() {
    this.successMessage = '';

    const modalRef = this.modalService.open(
      PurchaseCustomerMembershipModalComponent
    );
    modalRef.componentInstance.customer = this.customer;
  }

  public openPurchasePassModal() {
    this.successMessage = '';

    const modalRef = this.modalService.open(
      PurchaseCustomerEntryPassModalComponent
    );
    modalRef.componentInstance.customer = this.customer;
  }

  public openAddEntryPassModal() {
    this.successMessage = '';

    const modalRef = this.modalService.open(CreateCustomerEntryPassModalComponent);
    modalRef.componentInstance.customerId = this.customerId;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.successMessage = result.text;
      this.reloadEntryPassesSubject.next();
    };
  }

  public openReviewMembershipPendingStaffApprovalModal(customerMembershipId: number) {
    const modalRef = this.modalService.open(ReviewMembershipPendingStaffApprovalModalComponent);
    modalRef.componentInstance.customerMembershipId = customerMembershipId;
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = () => {
      this.reloadMembershipsSubject.next();
    };
  }

  public openLinkUnlinkedMembershipCustomerModal(unlinkedCustomer: CustomerMembershipUnlinkedCustomer) {
    if (this.loadingUnlinkedMembershipCustomer[unlinkedCustomer.id]) { return; }

    this.loadingUnlinkedMembershipCustomer[unlinkedCustomer.id] = true;

    this.inboxService
      .getUnlinkedMembershipCustomer(unlinkedCustomer.id)
      .then(unlinkedCustomerInboxItem => {
        const modalRef = this.modalService.open(ReviewUnlinkedMembershipCustomerModalComponent);
        modalRef.componentInstance.unlinkedCustomerInboxItem = unlinkedCustomerInboxItem;
        modalRef.componentInstance.savedCallback = () => {
          this.reloadMembershipsSubject.next();

          // Set the loading state back to true - will be reset when the memberships reload.
          this.loadingUnlinkedMembershipCustomer[unlinkedCustomer.id] = true;
        };
      })
      .catch(reason => {
        if (reason.status === 404) {
          // 404 indicates that the membership is no longer unlinked, so reload.
          this.reloadMembershipsSubject.next();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingUnlinkedMembershipCustomer[unlinkedCustomer.id] = false;
      });
  }

  public openLinkUnlinkedEntryPassCustomerModal(unlinkedCustomer: CustomerEntryPassUnlinkedCustomer) {
    if (this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id]) { return; }

    this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id] = true;

    this.inboxService
      .getUnlinkedEntryPassCustomer(unlinkedCustomer.id)
      .then(unlinkedCustomerInboxItem => {
        const modalRef = this.modalService.open(ReviewUnlinkedEntryPassCustomerModalComponent);
        modalRef.componentInstance.unlinkedCustomerInboxItem = unlinkedCustomerInboxItem;
        modalRef.componentInstance.savedCallback = () => {
          this.reloadEntryPassesSubject.next();

          // Set the loading state back to true - will be reset when the entry passes reload.
          this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id] = true;
        };
      })
      .catch(reason => {
        if (reason.status === 404) {
          // 404 indicates that the entry pass is no longer unlinked, so reload.
          this.reloadEntryPassesSubject.next();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingUnlinkedEntryPassCustomer[unlinkedCustomer.id] = false;
      });
  }

  public openAddGuestPassModal() {
    this.successMessage = '';

    const modalRef = this.modalService.open(CreateGuestPassModalComponent);
    modalRef.componentInstance.customerId = this.customerId;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.successMessage = result.text;
      this.reloadOwnedGuestPassesSubject.next();
    };
  }

  public openManageGuestPassModal(guestPass: GuestPass) {
    this.successMessage = '';

    const modalRef = this.modalService.open(ManageGuestPassModalComponent);
    modalRef.componentInstance.guestPass = guestPass;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.successMessage = result.text;
      this.reloadOwnedGuestPassesSubject.next();
    };
  }

  public getRecurringDiscountNameString(membership: CustomerMembership) {
    let nameString: string;
    if (membership.recurring_discount_name) {
      nameString = membership.recurring_discount_name;
    } else {
      nameString = membership.recurring_discount_code
    }

    if (membership.recurring_discount_association_name) {
      if (nameString) {
        nameString += ', ';
      }
      nameString += 'from ' + membership.recurring_discount_association_name;
      if (membership.recurring_discount_membership_name) {
        nameString += ' and ' + membership.recurring_discount_membership_name;
      }
    } else if (membership.recurring_discount_membership_name) {
      if (nameString) {
        nameString += ', ';
      }
      nameString += 'from ' + membership.recurring_discount_membership_name;
    }

    return nameString;
  }

  public getMemberNameById(membership: CustomerMembership, customerId: string): string {
    if (customerId[0] === 'u') {
      const customerIdInt = parseInt(customerId.slice(1), 10);

      for (const member of membership.unlinked_customers) {
        if (member.id === customerIdInt) {
          return member.first_name + ' ' + member.last_name;
        }
      }
    } else {
      const customerIdInt = parseInt(customerId, 10);

      for (const member of membership.all_customers) {
        if (member.id === customerIdInt) {
          return member.first_name + ' ' + member.last_name;
        }
      }
    }

    return '<unknown member>';
  }

  public displayEntryPassLinkingDetails(entryPass: CustomerEntryPass) {
    return (
      ((entryPass.customers_less_owner.length + entryPass.unlinked_customers.length) > 0) ||
      (entryPass.owner_association_id !== null)
    );
  }

  public getMembershipOwnerMember(membership: CustomerMembership) {
    for (const member of membership.all_customers) {
      if (member.id === membership.owner_id) { return member; }
    }
    return null;
  }

  public shouldShowMembersList(membership: CustomerMembership): boolean {
    return (
      !ownerIsOnlyMember(membership) ||
      membership.all_customers.some(m => m.is_individually_frozen)
    );
  }

  public cancelAddMemberNextBillDate(membership: CustomerMembership,
                                     member: CustomerMembershipCustomer) {
    if (this.loadingMembershipChange) { return; }

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Cancel Scheduled Addition';
    modalRef.componentInstance.messageText = 'Are you sure you want to cancel adding ' + member.first_name + ' ' + member.last_name + ' to this membership?';
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingMembershipChange = true;

      this.customerService
        .cancelAddGroupMembershipMemberNextBillDate(
          membership.id,
          member.id
        )
        .then(() => {
          this.successMessage = 'Scheduled addition canceled.';

          this.reloadMembershipsSubject.next();
        })
        .catch(reason => {
          if (reason.status === 400) {
            // 400 probably indicates that the local data is out of sync with
            // the server, so reload.
            this.reloadMembershipsSubject.next();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingMembershipChange = false; })
    }
  }

  public cancelRemoveMemberNextBillDate(membership: CustomerMembership,
                                        member: CustomerMembershipCustomer) {
    if (this.loadingMembershipChange) { return; }

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Cancel Scheduled Removal';
    modalRef.componentInstance.messageText = 'Are you sure you want to cancel removing ' + member.first_name + ' ' + member.last_name + ' from this membership?';
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingMembershipChange = true;

      this.customerService
        .cancelRemoveGroupMembershipMemberNextBillDate(
          membership.id,
          member.id
        )
        .then(() => {
          this.successMessage = 'Scheduled removal canceled.';

          this.reloadMembershipsSubject.next();
        })
        .catch(reason => {
          if (reason.status === 400) {
            // 400 probably indicates that the local data is out of sync with
            // the server, so reload.
            this.reloadMembershipsSubject.next();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingMembershipChange = false; })
    }
  }

  public membershipsLastPageNum() {
    if (!this.membershipsList) { return 1; }
    return Math.ceil(
      this.membershipsList.count /
      this.pageSizeMemberships
    );
  }

  public entryPassesLastPageNum() {
    if (!this.entryPassesList) { return 1; }
    return Math.ceil(
      this.entryPassesList.count /
      this.pageSizeEntryPasses
    );
  }

  public ownedGuestPassesLastPageNum() {
    if (!this.ownedGuestPassesList) { return 1; }
    return Math.ceil(
      this.ownedGuestPassesList.count /
      this.pageSizeOwnedGuestPasses
    );
  }

  public guestPassUsagesLastPageNum() {
    if (!this.guestPassUsagesList) { return 1; }
    return Math.ceil(
      this.guestPassUsagesList.count /
      this.pageSizeGuestPassUsages
    );
  }

  public sharedPassesLastPageNum() {
    if (!this.sharedPassesList) { return 1; }
    return Math.ceil(
      this.sharedPassesList.count /
      this.pageSizeSharedPasses
    );
  }

  public guestPassIsUnusable(guestPass: GuestPass) {
    return (
      propExists(guestPass.granting_membership_status) &&
      (guestPass.granting_membership_status !== CustomerMembershipStatus.active)
    );
  }

  private loadCustomer(customerId: number) {
    this.loadingCustomer = true;

    this.customerService
      .getCustomer(customerId)
      .then(customer => {
        this.customer = customer;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer = false; });
  }

  private loadCustomerProfileDetails(customerId: number) {
    this.loadingCustomerProfileDetails = true;

    this.customerService
      .getCustomerProfileDetails(customerId)
      .then(customerProfileDetails => {
        this.customerProfileDetails = customerProfileDetails;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomerProfileDetails = false; });
  }

  private loadMemberships(customerId: number) {
    this.loadingMemberships = true;

    this.customerService
      .getCustomerMemberships(
        customerId,
        this.membershipsPageNum,
        this.pageSizeMemberships
      )
      .then(results => {
        this.membershipsList = results;

        this.loadingUnlinkedMembershipCustomer = {};
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  private loadEntryPasses(customerId: number) {
    this.loadingEntryPasses = true;

    this.customerService
      .getCustomerEntryPasses(
        customerId,
        this.entryPassesPageNum,
        this.pageSizeEntryPasses
      )
      .then(results => {
        this.entryPassesList = results;

        this.loadingUnlinkedEntryPassCustomer = {};
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  private loadOwnedGuestPasses(customerId: number) {
    this.loadingOwnedGuestPasses = true;

    const filters: GuestPassFilters = {owner_id: customerId};
    if (this.ownedGuestPassesStatus) {
      filters.status = this.ownedGuestPassesStatus;
    }

    this.customerService
      .getGuestPasses(
        this.ownedGuestPassesPageNum,
        this.pageSizeOwnedGuestPasses,
        filters,
        true
      )
      .then(results => {
        this.ownedGuestPassesList = results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingOwnedGuestPasses = false; });
  }

  private loadGuestPassUsages(customerId: number) {
    this.loadingGuestPassUsages = true;

    this.customerService
      .getCustomerGuestPassUsages(
        customerId,
        this.guestPassUsagesPageNum,
        this.pageSizeGuestPassUsages
      )
      .then(results => {
        this.guestPassUsagesList = results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingGuestPassUsages = false; });
  }

  private loadSharedPasses(customerId: number) {
    this.loadingSharedPasses = true;

    this.customerService
      .getCustomerSharedPasses(
        customerId,
        this.sharedPassesPageNum,
        this.pageSizeSharedPasses
      )
      .then(results => {
        this.sharedPassesList = results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSharedPasses = false; });
  }
}
